import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import home from './Home.module.css';
import styles from './HomeAllocationChart.module.css';

/**
 * EAA-1814 (S3 rebuild — canonical frame 09): "Cấp tài khoản 7 ngày" (frame 09
 * lines 911-990) — a 7-day bar chart of new-account provisioning, labelled
 * T2..CN.
 *
 * NOT BUILT with real bars: `GET /dashboard`'s `kpis.provisioning` is a
 * current-moment snapshot (`{synced, pending, error}` counts over active
 * `Assignment` rows — see `useDashboard.ts` / `DashboardService._kpis`), not
 * a day-by-day time series. There is no field anywhere in the response this
 * chart could honestly bind to. The card shell is kept for layout parity;
 * the body renders the real snapshot instead of a fabricated 7-day trend —
 * still useful (today's synced/pending/error mix), just not what the frame
 * drew, and clearly labelled as such rather than presented as the 7-day view.
 *
 * Gated on `console.accounts.issue` — the capability that owns account
 * provisioning.
 */
export function HomeAllocationChart({
    can,
    provisioning,
}: {
    can: (key: string) => boolean;
    provisioning?: { synced: number; pending: number; error: number };
}) {
    const { t } = useTranslation();
    if (!can('console.accounts.issue')) {
        return null;
    }

    return (
        <div className={`${home.card} ${styles.card}`}>
            <div className={home.cardHeadText}>
                <h3 className={home.cardTitle}>{t('home.allocationChart.title')}</h3>
                <span className={home.cardSub}>{t('home.allocationChart.subtitle')}</span>
            </div>
            {provisioning ? (
                <div className={styles.snapshot}>
                    <div className={styles.row}>
                        <span className={`${styles.dot} ${styles.dotOk}`} aria-hidden="true" />
                        <span>{t('home.allocationChart.syncedLabel')}</span>
                        <strong className={styles.rowValue}>{provisioning.synced}</strong>
                    </div>
                    <div className={styles.row}>
                        <span className={`${styles.dot} ${styles.dotPending}`} aria-hidden="true" />
                        <span>{t('home.allocationChart.pendingLabel')}</span>
                        <strong className={styles.rowValue}>{provisioning.pending}</strong>
                    </div>
                    <div className={styles.row}>
                        <span className={`${styles.dot} ${styles.dotDanger}`} aria-hidden="true" />
                        <span>{t('home.allocationChart.errorLabel')}</span>
                        <strong className={styles.rowValue}>{provisioning.error}</strong>
                    </div>
                </div>
            ) : (
                <div className={home.unavailable}>
                    <span className={home.unavailableNote}>
                        {/* EAA-1845: Trans keeps the sentence whole for translators;
                            see the matching note in HomeAppCoverage.tsx. */}
                        <Trans
                            i18nKey="home.allocationChart.unavailableNote"
                            components={{
                                code: <code className={home.unavailableCode} />,
                                link: (
                                    <Link to="/console/accounts" className={home.unavailableLink} />
                                ),
                            }}
                        />
                    </span>
                </div>
            )}
        </div>
    );
}

export default HomeAllocationChart;
